import { OpenPanel } from "@openpanel/web";

/**
 * OpenPanel analytics for the Farsi surface.
 *
 * This app shipped on 21 Aug 2026 with no analytics at all, which made its own
 * gate unreadable: "nobody arrived" and "many arrived and none signed up" are
 * indistinguishable without it, and those two lead to opposite decisions on
 * 30 Oct. That is the whole reason this file exists.
 *
 * Copied deliberately from `apps/frontend/src/utils/analytics.ts` and
 * `apps/coding/src/analytics.ts`, including the bot filter. The numbers from
 * these apps get compared to one another, so they have to be filtered the same
 * way or the comparison means nothing.
 *
 * Two mistakes have silently produced zero events on this estate before: a bare
 * side-effect import gets tree-shaken because nothing consumes its exports (so
 * `initAnalytics()` is called explicitly from entry.client.tsx), and any CSP in
 * front of the app must allow connecting to openpanel-api.gazar.dev.
 */

const isLocalhost =
  typeof window !== "undefined" &&
  /^(localhost|127\.0\.0\.1|\[::1\])$/.test(window.location.hostname);

// Headless crawlers execute JavaScript, so the SDK counts them unless they are
// filtered here. gazar.dev's raw dashboard ran roughly 90% bots in Aug 2026.
function isLikelyBot(): boolean {
  if (typeof navigator === "undefined") return true;
  const nav = navigator as Navigator & { webdriver?: boolean };
  if (nav.webdriver) return true;
  const ua = nav.userAgent || "";
  if (!ua) return true;
  if (
    /HeadlessChrome|PhantomJS|Selenium|puppeteer|playwright|Electron|bot|crawler|spider|scrapy|python-requests|Go-http-client|MSIE [6-9]\./i.test(
      ua,
    )
  ) {
    return true;
  }
  if (Array.isArray(nav.languages) && nav.languages.length === 0) return true;
  return false;
}

const clientId = import.meta.env.VITE_PUBLIC_OPENPANEL_CLIENT_ID as string | undefined;
const apiUrl =
  (import.meta.env.VITE_PUBLIC_OPENPANEL_API_URL as string | undefined) ??
  "https://openpanel-api.gazar.dev";

let op: OpenPanel | null = null;

export function initAnalytics() {
  if (op || !clientId || isLocalhost || isLikelyBot()) return op;
  op = new OpenPanel({
    apiUrl,
    clientId,
    trackScreenViews: true,
    // On by default here, unlike the drills app: every course card on this page
    // leaves for maktabkhooneh.org, and how many people take that exit is one
    // of the two numbers this surface is judged on.
    trackOutgoingLinks: true,
    trackAttributes: false,
  });
  return op;
}

type Props = Record<string, unknown>;

function track(name: string, props?: Props) {
  op?.track(name, props);
}

/**
 * The events worth having, and only those.
 *
 * The question this surface has to answer by 30 Oct is not "how many visitors"
 * but "does the Maktabkhooneh audience transfer", so every event carries the
 * arrival route where one is known.
 */
export const analytics = {
  /** A course card clicked through to Maktabkhooneh. The reverse direction. */
  courseClick(props: { slug: string; src: string | null }) {
    track("fa_course_click", props);
  },

  /**
   * An email captured. The number the gate reads.
   *
   * `where` separates an address given at the end of an article from one given
   * on the landing page. Until 22 Aug 2026 the article pages carried no form at
   * all, so every address necessarily came from the landing page and the
   * dimension would have been constant. It is not constant any more, and the
   * two are different signals: the landing page converts arrivals who were sent
   * somewhere, an article converts a reader who was persuaded.
   */
  signup(props: { src: string | null; where: "home" | "article" }) {
    track("fa_signup", props);
  },

  /** A Farsi article opened. Paired with the backend viewCount, not a substitute. */
  articleRead(props: { slug: string; src: string | null }) {
    track("fa_article_read", props);
  },

  /**
   * A visitor who was actually here, as opposed to one who was counted.
   *
   * Added 29 Aug 2026 because the 27 Nov gate could not be read without it.
   * In the first 7.19 days of live data, seven devices arrived tagged
   * `src=mk` and SIX arrived tagged `src=telegram`; four of the seven and
   * all six of the six recorded session durations between 0 and 1100 ms, with
   * an event sequence of session_start, screen_view at +100 ms, session_end at
   * +1000 ms and nothing in between. That is the signature of a prefetch or an
   * in-app-browser preload, not of a person, and `session_start` cannot tell
   * the two apart. A gate that reads arrivals therefore cannot distinguish
   * "the catalogue sent seven humans" from "it sent three humans and four
   * prefetches", and those two readings lead to opposite decisions.
   *
   * Fires at most ONCE per session, so it counts people rather than pageviews,
   * which is the unit the gate is written in. It requires both five seconds of
   * VISIBLE time, accumulated only while the tab is actually foregrounded so a
   * background preload never accrues any, and one deliberate interaction:
   * a scroll, a pointer, a key or a touch. A prefetch produces neither.
   *
   * `src` is on it deliberately. Counting engaged visitors without their
   * arrival route would answer "are these real people" while leaving "which
   * channel sent them" exactly as unanswerable as it is now.
   */
  engaged(props: { src: string | null; path: string }) {
    track("fa_engaged", props);
  },

  /**
   * The Telegram group clicked.
   *
   * Worth its own event rather than leaning on `link_out`, because Telegram is
   * the one outbound destination that COMPETES with the email form for the same
   * visitor. `where` separates the quiet chrome links from the deliberate CTA,
   * so November can answer whether adding the group grew the audience or simply
   * moved signups off the number the gate reads.
   */
  telegramClick(props: { where: "cta" | "chrome"; src: string | null }) {
    track("fa_telegram_click", props);
  },
};
